import { useState, useEffect } from "react";
import { transactionsApi, profileApi, receiptsApi } from "../api/client";
import { C, fmt, Card, Tag, TagInput } from "./theme";
import ReceiptWidget from "./ReceiptWidget";

// Lazy-fetch a presigned view URL for a receipt thumbnail, keyed by s3Key.
const ReceiptThumb = ({ s3Key }) => {
  const [url, setUrl] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!s3Key) return;
    let alive = true;
    receiptsApi.getViewUrl(s3Key).then(r => alive && setUrl(r.url)).catch(() => {});
    return () => { alive = false; };
  }, [s3Key]);
  if (!url) return null;
  return (
    <>
      <img src={url} alt="receipt" onClick={() => setOpen(true)}
        style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 6, flexShrink: 0, cursor: "zoom-in", border: `1px solid ${C.border}` }} />
      {open && (
        <div onClick={() => setOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 300, cursor: "zoom-out" }}>
          <img src={url} alt="receipt" style={{ maxWidth: "100%", maxHeight: "100%", borderRadius: 8 }} />
        </div>
      )}
    </>
  );
};

const CATEGORIES = {
  income:  ["Salary", "Business", "Freelance", "Rent", "Dividends", "Interest Earned", "Other Income"],
  expense: ["Rent", "Housing", "Food", "Transport", "Healthcare", "Education", "Entertainment", "Utilities", "EMI/Loan", "Insurance", "Clothing", "Personal Care", "Investments", "Donation", "Other"],
};

export default function PendingInbox({ familyMembers, onConfirmed }) {
  const [pending, setPending]       = useState([]);
  const [scheduled, setScheduled]   = useState([]);
  const [alias, setAlias]           = useState(null);
  const [messages, setMessages]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [confirming, setConfirming] = useState({});
  const [overrides, setOverrides]   = useState({});
  const [showSetup, setShowSetup]   = useState(false);

  const activeMembers = familyMembers.filter(m => m.name && m.name.trim());

  const load = async () => {
    setLoading(true);
    try {
      const [pendingRes, scheduledRes, aliasRes, messagesRes] = await Promise.all([
        transactionsApi.listPending(),
        transactionsApi.listScheduled().catch(() => ({ items: [] })),
        profileApi.getEmailAlias(),
        profileApi.getMessages().catch(() => ({ messages: [] })),
      ]);
      setPending(pendingRes.items || []);
      setScheduled(scheduledRes.items || []);
      setAlias(aliasRes);
      setMessages(messagesRes.messages || []);
    } finally {
      setLoading(false);
    }
  };

  const dismissMessage = async (sk) => {
    try {
      await profileApi.dismissMessage(sk);
      setMessages(m => m.filter(x => x.sk !== sk));
    } catch (e) { alert(e.message); }
  };

  useEffect(() => { load(); }, []);

  const handleConfirm = async (t, isScheduled = false) => {
    setConfirming(c => ({ ...c, [t.id]: true }));
    try {
      const o = overrides[t.id] || {};
      await transactionsApi.confirm(t.id, t.txnDate, o);
      if (isScheduled) setScheduled(p => p.filter(x => x.id !== t.id));
      else             setPending(p => p.filter(x => x.id !== t.id));
      onConfirmed?.();
    } catch (e) {
      alert(e.message);
    } finally {
      setConfirming(c => ({ ...c, [t.id]: false }));
    }
  };

  const handleReject = async (t, isScheduled = false) => {
    if (!window.confirm(isScheduled
      ? "Reject this scheduled payment? It will not be tracked."
      : "Reject this transaction? It will be deleted.")) return;
    setConfirming(c => ({ ...c, [t.id]: true }));
    try {
      await transactionsApi.delete(t.id, t.txnDate);
      if (isScheduled) setScheduled(p => p.filter(x => x.id !== t.id));
      else             setPending(p => p.filter(x => x.id !== t.id));
    } catch (e) {
      alert(e.message);
    } finally {
      setConfirming(c => ({ ...c, [t.id]: false }));
    }
  };

  const setOverride = (id, field, value) =>
    setOverrides(o => ({ ...o, [id]: { ...o[id], [field]: value } }));

  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>
          📥 Auto-imported from email
          {pending.length > 0 && <Tag c={C.gold}>{pending.length} pending</Tag>}
        </div>
        <button onClick={() => setShowSetup(s => !s)}
          style={{ background: "transparent", border: "none", color: C.blue, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
          {showSetup ? "Hide setup" : "How to set up"}
        </button>
      </div>

      <div style={{ marginBottom: 14 }}>
        <ReceiptWidget onReceiptSaved={load} />
      </div>

      {showSetup && alias && (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 14, marginBottom: 14, fontSize: 13, color: C.muted, lineHeight: 1.7 }}>
          <div style={{ fontWeight: 700, color: C.text, marginBottom: 8 }}>Your unique forwarding address:</div>
          <div style={{ background: C.card, padding: "8px 12px", borderRadius: 6, fontFamily: "monospace", fontSize: 13, color: C.accent, userSelect: "all", marginBottom: 12, wordBreak: "break-all" }}>
            {alias.forwardAddress}
          </div>
          <div style={{ fontWeight: 700, color: C.text, marginBottom: 6 }}>Gmail setup (one-time, ~2 min):</div>
          <ol style={{ paddingLeft: 18, margin: 0 }}>
            <li>Open Gmail → Settings (gear icon) → See all settings → <strong>Filters and Blocked Addresses</strong></li>
            <li>Create a new filter with <strong>From:</strong> (paste this combined list)
              <div style={{ background: C.card, padding: "6px 10px", borderRadius: 4, marginTop: 4, fontFamily: "monospace", fontSize: 11, wordBreak: "break-all", userSelect: "all" }}>
                alerts@hdfcbank.bank.in OR cbsalerts.sbi@alerts.sbi.bank.in OR neftinfo.itps@alerts.sbi.bank.in OR customernotification@icici.bank.in
              </div>
            </li>
            <li><strong>Apply the label</strong> and <strong>Forward to</strong>: paste your TrackFin address above</li>
            <li>Gmail sends a verification email — the code/link appears in this Pending Inbox</li>
          </ol>
          <div style={{ marginTop: 10, padding: 10, background: C.blue + "15", border: `1px solid ${C.blue}33`, borderRadius: 6, fontSize: 11, color: C.text, lineHeight: 1.6 }}>
            💡 <strong>Tip:</strong> Search your Gmail inbox for past transactions to find the exact sender address your bank uses (each branch / card type can differ). Add any extra addresses to the filter <strong>From:</strong> line above using <code style={{ background: C.card, padding: "0 4px", borderRadius: 2 }}>OR</code> between them.
          </div>
          <div style={{ marginTop: 10, fontSize: 12, color: C.muted }}>
            Currently supported: HDFC (bank + credit card), ICICI (bank + NEFT), SBI (bank + NEFT). More banks coming soon — share a sample email and we'll add it.
          </div>
        </div>
      )}

      {/* System messages (e.g., Gmail forwarding confirmation code) */}
      {messages.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
          {messages.map(m => (
            <div key={m.sk} style={{ background: C.gold + "11", border: `1px solid ${C.gold}55`, borderRadius: 8, padding: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: C.gold, fontWeight: 700, marginBottom: 4 }}>
                    {m.type === "gmail_forwarding_confirmation" ? "📬 Gmail forwarding verification" :
                     m.type === "scheduled_payment_notice"      ? "📅 Upcoming scheduled payment" :
                                                                  "System message"}
                  </div>
                  <div style={{ fontSize: 13, color: C.text, marginBottom: 8, wordBreak: "break-word" }}>{m.subject}</div>
                  {m.type === "scheduled_payment_notice" && (m.amount || m.vendor || m.scheduledDate) && (
                    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: 10, marginBottom: 8, fontSize: 12, color: C.text, lineHeight: 1.6 }}>
                      {m.amount && <div><strong>Amount:</strong> ₹{m.amount}</div>}
                      {m.vendor && <div><strong>For:</strong> {m.vendor}</div>}
                      {m.scheduledDate && <div><strong>Scheduled:</strong> {m.scheduledDate}</div>}
                      <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>The actual debit will be auto-imported when it happens.</div>
                    </div>
                  )}
                  {m.verificationCode && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: 0.5, marginBottom: 4 }}>VERIFICATION CODE</div>
                      <div style={{ display: "inline-block", background: C.card, padding: "6px 14px", borderRadius: 6, fontFamily: "monospace", fontSize: 16, fontWeight: 700, color: C.accent, userSelect: "all", border: `1px solid ${C.border}` }}>
                        {m.verificationCode}
                      </div>
                    </div>
                  )}
                  {m.verificationLink && (
                    <div>
                      <a href={m.verificationLink} target="_blank" rel="noopener noreferrer"
                        style={{ fontSize: 12, color: C.blue, fontWeight: 600, textDecoration: "none" }}>
                        Click to verify →
                      </a>
                    </div>
                  )}
                </div>
                <button onClick={() => dismissMessage(m.sk)}
                  style={{ background: "transparent", border: "none", color: C.muted, cursor: "pointer", fontSize: 18, padding: 0, lineHeight: 1 }}>
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* SCHEDULED — upcoming bills / e-mandates */}
      {scheduled.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: C.text, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
            📅 Upcoming Bills <Tag c={C.blue}>{scheduled.length}</Tag>
            <span style={{ fontSize: 11, color: C.muted, fontWeight: 400 }}>· auto-detected from e-mandate notices</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {scheduled.map(t => {
              const o = overrides[t.id] || {};
              const date     = o.txnDate          ?? t.txnDate;
              const recurring = o.isRecurring     ?? (t.isRecurring !== false);
              const interval  = o.recurringInterval ?? (t.recurringInterval || "monthly");
              const cat       = o.category        ?? t.category;
              const mem       = o.member          ?? t.member;
              return (
                <div key={t.id} style={{ background: C.blue + "08", border: `1px solid ${C.blue}33`, borderRadius: 8, padding: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                        <Tag c={C.blue}>{t.bank || "Bank"}</Tag>
                        <Tag c={C.gold}>⏰ scheduled</Tag>
                        {recurring && <Tag c={C.accent}>🔁 {interval}</Tag>}
                      </div>
                      <div style={{ fontSize: 13, color: C.text, marginBottom: 4 }}>{t.note || "—"}</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: C.red }}>−{fmt(t.amount)}</div>
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8, marginTop: 12 }}>
                    <div>
                      <label style={{ fontSize: 10, color: C.muted, fontWeight: 700, letterSpacing: 0.5, display: "block", marginBottom: 4 }}>SCHEDULED DATE</label>
                      <input type="date" value={date}
                        onChange={e => setOverride(t.id, "txnDate", e.target.value)}
                        style={{ width: "100%", background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: "7px 10px", color: C.text, fontSize: 12 }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 10, color: C.muted, fontWeight: 700, letterSpacing: 0.5, display: "block", marginBottom: 4 }}>CATEGORY</label>
                      <select value={cat} onChange={e => setOverride(t.id, "category", e.target.value)}
                        style={{ width: "100%", background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: "7px 10px", color: C.text, fontSize: 12 }}>
                        {CATEGORIES.expense.map(c => <option key={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 10, color: C.muted, fontWeight: 700, letterSpacing: 0.5, display: "block", marginBottom: 4 }}>MEMBER</label>
                      <select value={mem} onChange={e => setOverride(t.id, "member", e.target.value)}
                        style={{ width: "100%", background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: "7px 10px", color: C.text, fontSize: 12 }}>
                        {activeMembers.map(m => <option key={m.role} value={m.role}>{m.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 10, color: C.muted, fontWeight: 700, letterSpacing: 0.5, display: "block", marginBottom: 4 }}>RECURRING</label>
                      <select value={recurring ? interval : "none"}
                        onChange={e => {
                          const v = e.target.value;
                          if (v === "none") { setOverride(t.id, "isRecurring", false); }
                          else              { setOverride(t.id, "isRecurring", true); setOverride(t.id, "recurringInterval", v); }
                        }}
                        style={{ width: "100%", background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: "7px 10px", color: C.text, fontSize: 12 }}>
                        <option value="none">One-time</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
                    <button onClick={() => handleConfirm(t, true)} disabled={confirming[t.id]}
                      style={{ flex: 1, padding: "8px 14px", background: C.accent, color: "#fff", border: "none", borderRadius: 6, fontWeight: 600, fontSize: 12, cursor: "pointer", opacity: confirming[t.id] ? 0.6 : 1 }}>
                      {confirming[t.id] ? "..." : "✓ Accept"}
                    </button>
                    <button onClick={() => handleReject(t, true)} disabled={confirming[t.id]}
                      style={{ padding: "8px 14px", background: "transparent", color: C.red, border: `1px solid ${C.red}55`, borderRadius: 6, fontWeight: 600, fontSize: 12, cursor: "pointer" }}>
                      ✕ Reject
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ padding: 20, textAlign: "center", color: C.muted, fontSize: 13 }}>Loading...</div>
      ) : pending.length === 0 ? (
        <div style={{ padding: 24, textAlign: "center", color: C.muted, fontSize: 13 }}>
          {alias
            ? "No pending transactions. Bank emails forwarded to your address will appear here for confirmation."
            : "Click \"How to set up\" above to enable auto-import."}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {pending.map(t => {
            const o = overrides[t.id] || {};
            const cat = o.category || t.category;
            const mem = o.member   || t.member;
            return (
              <div key={t.id} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 200, display: "flex", gap: 12 }}>
                    {t.receiptS3Key && <ReceiptThumb s3Key={t.receiptS3Key} />}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                        {t.source === "receipt" ? <Tag c={C.purple}>📸 Receipt</Tag> : <Tag c={C.blue}>{t.bank || "—"}</Tag>}
                        <Tag c={t.type === "income" ? C.success : C.red}>{t.type}</Tag>
                        {t.paymentMethod === "credit_card" && <Tag c={C.purple}>💳 Credit Card</Tag>}
                        <span style={{ fontSize: 12, color: C.muted }}>{t.txnDate}</span>
                      </div>
                      <div style={{ fontSize: 13, color: C.text, marginBottom: 2 }}>{t.note || "—"}</div>
                      {t.source === "receipt" ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 14, color: C.muted }}>₹</span>
                          <input type="number" step="0.01" min="0"
                            value={(overrides[t.id]?.amount) ?? (t.amount || "")}
                            onChange={e => setOverride(t.id, "amount", e.target.value === "" ? "" : Number(e.target.value))}
                            placeholder="Enter amount"
                            style={{ background: C.card, border: `1px solid ${(overrides[t.id]?.amount ?? t.amount) > 0 ? C.border : C.gold}`, borderRadius: 6, padding: "6px 10px", color: t.type === "income" ? C.success : C.red, fontSize: 18, fontWeight: 700, width: 140 }} />
                        </div>
                      ) : (
                        <div style={{ fontSize: 18, fontWeight: 700, color: t.type === "income" ? C.success : C.red }}>
                          {t.type === "income" ? "+" : "−"}{fmt(t.amount)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 12 }}>
                  <div>
                    <label style={{ fontSize: 10, color: C.muted, fontWeight: 700, letterSpacing: 0.5, display: "block", marginBottom: 4 }}>CATEGORY</label>
                    <select value={cat} onChange={e => setOverride(t.id, "category", e.target.value)}
                      style={{ width: "100%", background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: "7px 10px", color: C.text, fontSize: 12 }}>
                      {(CATEGORIES[t.type] || []).map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 10, color: C.muted, fontWeight: 700, letterSpacing: 0.5, display: "block", marginBottom: 4 }}>MEMBER</label>
                    <select value={mem} onChange={e => setOverride(t.id, "member", e.target.value)}
                      style={{ width: "100%", background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: "7px 10px", color: C.text, fontSize: 12 }}>
                      {activeMembers.map(m => <option key={m.role} value={m.role}>{m.name}</option>)}
                    </select>
                  </div>
                </div>

                {/* Note + tags */}
                <div style={{ marginTop: 10 }}>
                  <label style={{ fontSize: 10, color: C.muted, fontWeight: 700, letterSpacing: 0.5, display: "block", marginBottom: 4 }}>NOTE</label>
                  <input value={(overrides[t.id]?.note) ?? t.note ?? ""}
                    onChange={e => setOverride(t.id, "note", e.target.value)}
                    placeholder="Add or edit note"
                    style={{ width: "100%", background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: "7px 10px", color: C.text, fontSize: 12 }} />
                </div>
                <div style={{ marginTop: 8 }}>
                  <label style={{ fontSize: 10, color: C.muted, fontWeight: 700, letterSpacing: 0.5, display: "block", marginBottom: 4 }}>TAGS</label>
                  <TagInput tags={(overrides[t.id]?.tags) ?? t.tags ?? []}
                    onChange={tags => setOverride(t.id, "tags", tags)}
                    placeholder="e.g. refundable, business, vacation" />
                </div>
                {t.rawText && (
                  <details style={{ marginTop: 10 }}>
                    <summary style={{ cursor: "pointer", fontSize: 11, color: C.muted, fontWeight: 600 }}>
                      Show OCR text (debug)
                    </summary>
                    <pre style={{ marginTop: 6, padding: 10, background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, fontFamily: "monospace", fontSize: 10, lineHeight: 1.5, maxHeight: 220, overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word", color: C.muted }}>{t.rawText}</pre>
                  </details>
                )}

                <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
                  <button onClick={() => handleConfirm(t)} disabled={confirming[t.id]}
                    style={{ flex: 1, padding: "8px 14px", background: C.accent, color: "#fff", border: "none", borderRadius: 6, fontWeight: 600, fontSize: 12, cursor: "pointer", opacity: confirming[t.id] ? 0.6 : 1 }}>
                    {confirming[t.id] ? "..." : "✓ Confirm"}
                  </button>
                  <button onClick={() => handleReject(t)} disabled={confirming[t.id]}
                    style={{ padding: "8px 14px", background: "transparent", color: C.red, border: `1px solid ${C.red}55`, borderRadius: 6, fontWeight: 600, fontSize: 12, cursor: "pointer" }}>
                    ✕ Reject
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
